// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Flex } from "@mantine/core";
import dayjs from "dayjs";

import CurrentRoute from "./modules/CurrentRoute";
import SavedRoutes from "./modules/SavedRoutes";
import RouteListCustomers from "./modules/RouteListCustomers";

import { ReactComponent as RouteIcon } from "assets/route.svg";
import { ReactComponent as ListIcon } from "assets/list.svg";
import { ReactComponent as GoIcon } from "assets/go.svg";

import Text from "components/Text/Text";
import styles from "./MapRouter.module.scss";
import { store } from "stores/Store";
import useExternalApiService from "helpers/ExternalApiService";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import RoutesTab from "Modules/PractitionerBaseballCard/components/RoutesTab";
import UnsavedChangesModal from "./modules/CurrentRoute/components/UnsavedChangesModal";
import { modals } from "@mantine/modals";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const bigger = (a?: number, b?: number) => Math.max(a || 0, b || 0);

export enum Tab {
  CURRENT_ROUTE = "current_route",
  SAVED_ROUTES = "saved_routes",
  LIST_CUSTOMERS = "list_customers"
}

interface IMapRouterProps {   
  setSelectedView: (view: string) => void;
  tab?: Tab;
  setShowUnsavedChangesModal: (show: boolean) => void;
  showUnsavedChangesModal: boolean;
  hasRouteChanged: boolean;
  setHasRouteChanged: (hasRouteChanged: boolean) => void;
}

const MapRouter = ({ setSelectedView, tab = Tab.CURRENT_ROUTE, setShowUnsavedChangesModal, showUnsavedChangesModal,
                     hasRouteChanged, setHasRouteChanged }: IMapRouterProps) => {
  const [selectedTab, setSelectedTab] = React.useState<Tab>(tab);
  const [routeName, setRouteName] = React.useState<string>("Current Route");
  const [currentRoute, setCurrentRoute] = React.useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isOpen, setIsOpen] = React.useState(false);
  const [pendingTab, setPendingTab] = React.useState<Tab | null>(null);
  const [pendingRouteId, setPendingRouteId] = React.useState<number | null>(null);
  const [clickedClear, setClickedClear] = React.useState(false);
  const [routeLength, setRouteLength] = React.useState(0);
  const [routeDate, setRouteDate] = React.useState<string | null>(null);

  const navigate = useNavigate();

  const selectedPractitioners = store.selectedPractitioners;

  React.useEffect(() => {
    if(currentRoute) {
      setRouteName(currentRoute.name);
      setRouteDate(currentRoute.route_date);
      store.setCurrentActiveRoute(currentRoute);
    } else {
      if (store.currentActiveRoute) {
        setCurrentRoute(store.currentActiveRoute);
      }
    }
  }, [currentRoute]);


  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);

  const handleNavigation = (tab: Tab, routeId: number, hotfixBypass = false) => {
    if (hasRouteChanged && !hotfixBypass) {
      setShowUnsavedChangesModal(true);
      setPendingTab(tab);
      setPendingRouteId(routeId);
    } else {
      setSelectedTab(tab);
      store.setSelectedPractitioners([]);
      getCurrentRoute(routeId);
      store.setSelectedPractitioners([]);
      setCurrentRoute(null);
      store.setCurrentActiveRoute(null);
      setRouteName("Current Route");
    }
  };

  const handleConfirmNavigation = () => {
    if (pendingTab) {
      setSelectedTab(pendingTab);
      getCurrentRoute(pendingRouteId || 0);
      store.setSelectedPractitioners([]);
      setPendingTab(null);
    }
    setShowUnsavedChangesModal(false);
    setHasRouteChanged(false);
  };

  const handleCancelNavigation = () => {
    setShowUnsavedChangesModal(false);
    setPendingTab(null);
    setClickedClear(false);
  };
  const onCustomerAdd = (customer: any) => {
    for (let i = 0; i < customer.length; i++) {
      customer[i].duration = 15;
      console.log("customer", customer[i]);
      store.addToRoute(customer[i]);
    }
    setHasRouteChanged(true);
    setSelectedTab(Tab.CURRENT_ROUTE);
  }
  const renderTabHeader = () => {
    switch (selectedTab) {
      case Tab.CURRENT_ROUTE:
        return (
          <>
            <Flex w="100%" justify="space-between" align="center">
              <div>
                {routeName ? <Text type="header">{routeName}</Text> : <Text type="header">Current Route</Text>}
                {routeDate ? <Text color="#7d7d7d" type="subHeader">{dayjs(routeDate).format('MMMM D, YYYY')}</Text> : null}
                <Text color="#7d7d7d" type="subHeader">
                  {`${routeLength} places to visit`}
                </Text>
              </div>
              {selectedPractitioners?.length > 0 && (
                <Button
                  py={2}
                  color="#00DC49"
                  onClick={() => {
                    modals.closeAll();
                    navigate("/navigation-mode");
                  }}
                >
                  <GoIcon />
                </Button>
              )}
            </Flex>
          </>
        )
      case Tab.SAVED_ROUTES:
        return <Text type="header">Your Routes</Text>
      case Tab.LIST_CUSTOMERS:
        return <Text type="header">Add Account to Route</Text>
    }
  }

  const onRouteCardClicked = (routeId: number, hotfixBypass = false) => {
    clearCurrentRoute();
    handleNavigation(Tab.CURRENT_ROUTE, routeId, hotfixBypass);
  }

  const getCurrentRoute = async (routeId: number) => {
    const data = await postData("/api/get_route_by_id", {
      "route_id": routeId
    });
    setCurrentRoute(data.route);
    setRouteLength(data?.route?.locations?.length)
  }

  const onRouteChange = (v: number) => {
    setRouteLength(v);
  }

  const clearCurrentRoute = () => {
    setCurrentRoute(null);
    setRouteDate(null);
    store.setCurrentActiveRoute(null);
    setRouteLength(0);
    setRouteName("Current Route");
    store.setCurrentDirections([]);
  }

  const renderTabContent = () => {
    switch (selectedTab) {
      case Tab.CURRENT_ROUTE:
        return <CurrentRoute 
          clearCurrentRoute={clearCurrentRoute}
          currentRoute={currentRoute} 
          setSelectedTab={setSelectedTab} 
          setSelectedView={setSelectedView} 
          currentRouteName={routeName}
          setHasRouteChanged={setHasRouteChanged}
          setShowUnsavedChangesModal={setShowUnsavedChangesModal}
          hasRouteChanged={hasRouteChanged}
          showUnsavedChangesModal={showUnsavedChangesModal}
          clickedClear={clickedClear}
          setClickedClear={setClickedClear}
          onRouteChange={onRouteChange}
          onRouteSave={(id) => {
            if (id) {
              onRouteCardClicked(id, true);
            }
          }}
          setRouteName={setRouteName}
        />
      case Tab.SAVED_ROUTES:
        return <SavedRoutes onRouteCardClicked={onRouteCardClicked} />
      case Tab.LIST_CUSTOMERS:
        return <RouteListCustomers 
                preSelectedPractitioners={selectedPractitioners}
                onCustomerAdd={onCustomerAdd} />
    }
  }

  return (
    <div className={styles.wrapper}>      
      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        onClose={handleCancelNavigation}
        onConfirm={handleConfirmNavigation}
      />
      <div className={styles.headerWrapper}>
        {renderTabHeader()}
      </div>
      <div className={styles.horizontalScrollWrapper}>
        <div
          onClick={() => setSelectedTab(Tab.CURRENT_ROUTE)}
          className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.CURRENT_ROUTE ? styles.selectedTab : styles.disabledTab}`}
        >
          <RouteIcon />
          <p>Current Route</p>
        </div>
        <div
          onClick={() => setSelectedTab(Tab.SAVED_ROUTES)}
          className={`${styles.horizontalScrollItemWrapper} ${selectedTab === Tab.SAVED_ROUTES ? styles.selectedTab : styles.disabledTab}`}
        >
          <ListIcon />
          <p>Saved Routes</p>
        </div>
      </div>
      {renderTabContent()}
    </div>
  )
}

export default MapRouter;